import React from 'react';
import { Modal, List, Button, Space, Tag, Typography, Alert } from 'antd';
import { UserOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

// Shown when someone tries to register a patient who already has a file. The
// point is to make picking the existing profile the easy path - registering a
// second time is still possible (people do share a name) but has to be chosen
// deliberately.
const DuplicatePatientModal = ({
    open,
    matches = [],
    onSelect,
    onCreateAnyway,
    onCancel,
    creating = false
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                    {t('patientAlreadyExists') || 'This patient already exists'}
                </Space>
            }
            open={open}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t('cancel') || 'Cancel'}
                </Button>,
                onCreateAnyway && (
                    <Button key="force" danger loading={creating} onClick={onCreateAnyway}>
                        {t('registerAnyway') || 'Register as a new patient anyway'}
                    </Button>
                )
            ].filter(Boolean)}
            width={620}
        >
            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                    t('duplicatePatientHint') ||
                    'Choose the existing profile so this visit joins their history.'
                }
            />

            <List
                dataSource={matches}
                rowKey="_id"
                renderItem={(patient) => (
                    <List.Item
                        actions={[
                            <Button type="primary" onClick={() => onSelect(patient)}>
                                {t('useThisPatient') || 'Use this patient'}
                            </Button>
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<UserOutlined style={{ fontSize: 20 }} />}
                            title={
                                <Space wrap>
                                    <Text strong>{patient.name}</Text>
                                    {patient.gender && <Tag>{t(patient.gender) || patient.gender}</Tag>}
                                </Space>
                            }
                            description={
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary">{patient.phoneNumber}</Text>
                                    {patient.doctorId?.name && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('doctor') || 'Doctor'}: {patient.doctorId.name}
                                        </Text>
                                    )}
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />
        </Modal>
    );
};

export default DuplicatePatientModal;
